/**
 * CR-W3-01-10 (registry contract `CommandContext.at`): the doc beat (float) a context menu was opened at.
 *
 * Computed when the menu opens (`ui.openContextMenu`, while the lane hover still describes the clicked point):
 * the hovered note's beat, or the hovered lane beat snapped to the editor grid; else the beat under the pointer in a
 * timeline column that publishes no hover (motion columns, the time gutter), on the timeline's shared time axis.
 * Commands run from the menu (source 'context') receive it as `ctx.at`. `features/batch/contextBeat.ts` keeps
 * working until its callers move over; a right-click where no beat can be read leaves `at` unset and those callers
 * fall back to the playhead.
 */

import { beatFromF64Snapped, Beats } from '../core/time/beat';
import type { AppServices } from './registry/types';

type Trigger = { readonly clientX?: number; readonly clientY?: number; readonly target?: EventTarget | null };

function snapDivOf(s: AppServices): number {
  const div = s.editor.getState().snap.div;
  return Number.isSafeInteger(div) && div >= 1 ? div : 4;
}

/** The beat under client Y in the timeline column that holds `target` (null when there is none). */
function columnBeatF64(s: AppServices, target: EventTarget | null, clientY: number): number | null {
  const host = s.timeline;
  if (host === null || typeof Element === 'undefined' || !(target instanceof Element)) return null;
  const canvas = target.closest('[data-tl-col]')?.querySelector('canvas[data-layer]') ?? null;
  if (canvas === null) return null;
  const axis = host.axis;
  const y = clientY - canvas.getBoundingClientRect().top;
  if (!(y >= 0 && y <= axis.heightPx)) return null;
  const beat = axis.beatAtY(y);
  if (!Number.isFinite(beat)) return null;
  try {
    return Beats.toF64(axis.snap(Math.max(0, beat)));
  } catch {
    return null;
  }
}

/** The beat a context menu opens at (float, snapped), or null when none can be read. */
export function contextMenuBeatF64(s: AppServices, event: Trigger | null): number | null {
  if (event === null) return null;
  try {
    const st = s.editor.getState();
    const h = st.hover;
    if (h !== null) {
      if (h.kind === 'note') {
        const t = s.store?.doc.notes.get(h.note)?.t ?? null;
        if (t !== null) return Beats.toF64(t);
      } else if (Number.isFinite(h.beat)) {
        return Beats.toF64(beatFromF64Snapped(Math.max(0, h.beat), snapDivOf(s)));
      }
    }
    const clientY = event.clientY;
    if (typeof clientY !== 'number') return null;
    return columnBeatF64(s, event.target ?? null, clientY);
  } catch {
    return null;
  }
}
